import { Box, useBreakpointValue } from '@chakra-ui/react';
import { AnimatePresence, motion, useIsPresent, useReducedMotion, type Transition, type Variants } from 'framer-motion';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { matchPath, useLocation, useNavigationType } from 'react-router-dom';
import { mobileTabIdForPath } from './mobileTabStacks';
import { getRouteChrome } from './routeChrome';
import { consumeAppBackTransition } from './routeTransitionIntent';

type RouterLocation = ReturnType<typeof useLocation>;
type TransitionDirection = -1 | 0 | 1;

type RouteSnapshot = {
  pathname: string;
  historyIndex: number | undefined;
};

type MobileRouteTransitionProps = {
  children: (location: RouterLocation) => ReactNode;
  /**
   * Drive transitions from a frozen/keep-alive location instead of the router.
   * Used by {@link MobileTabKeepAlive} so hidden tabs don't react to other tabs'
   * navigations.
   */
  location?: RouterLocation;
  /** When false, never slide (inactive keep-alive tab panels). Default true. */
  transitionsEnabled?: boolean;
};

/**
 * How far the *underlying* screen parallax-shifts while the top screen covers
 * it (fraction of viewport width). Matches the ~30% UIKit / React-Navigation
 * `forHorizontalIOS` feel.
 */
const PARALLAX = 0.28;
const PARALLAX_PCT = `${-PARALLAX * 100}%`;

/** Leading-edge shadow cast by the top card onto the screen beneath it. */
const CARD_SHADOW = '-12px 0 28px rgba(0, 0, 0, 0.28)';
const FLAT_SHADOW = '0px 0 0px rgba(0, 0, 0, 0)';

/**
 * z-order rule: the screen travelling the *full* width (0 ↔ 100%) is always on
 * top, the one doing the short parallax shift sits beneath it.
 *  - push    (dir  1): incoming travels 100%→0  → on top.
 *  - pop     (dir -1): outgoing travels 0→100%   → on top.
 *  - instant (dir  0): incoming stays on top. A no-slide exit used to keep the
 *    outgoing card at z-index 2, so a viewport-fixed match board stayed painted
 *    over Match History.
 */
const routeTransitionVariants: Variants = {
  enter: (dir: TransitionDirection) => ({
    x: dir > 0 ? '100%' : dir < 0 ? PARALLAX_PCT : 0,
    zIndex: dir < 0 ? 1 : 2,
    boxShadow: dir === 0 ? FLAT_SHADOW : CARD_SHADOW,
  }),
  // `center` keeps the top card's stacking for the whole tween: on push and on
  // an instant swap the incoming screen stays above (2); on pop the incoming
  // destination stays below (1) the outgoing card. `popLayout` makes the exiting
  // layer absolute, which would otherwise paint above an equal-z in-flow sibling.
  center: (dir: TransitionDirection) => ({
    x: 0,
    zIndex: dir < 0 ? 1 : 2,
    boxShadow: FLAT_SHADOW,
  }),
  // dir === 0: instant same-tree swap (no slide). Keep x at 0 so non-animated
  // pathname changes don't flash the outgoing screen off to the right, and keep
  // that outgoing card underneath the page that replaced it.
  exit: (dir: TransitionDirection) => ({
    x: dir > 0 ? PARALLAX_PCT : dir < 0 ? '100%' : 0,
    zIndex: dir < 0 ? 2 : 1,
    boxShadow: dir === 0 ? FLAT_SHADOW : CARD_SHADOW,
  }),
};

/** Instant swap when the stable mobile tree is not running a push/pop slide. */
const immediateRouteTransition: Transition = {
  duration: 0,
  zIndex: { duration: 0 },
};

const routeTransition: Transition = {
  duration: 0.26,
  ease: [0.32, 0.72, 0, 1],
  // z-order must flip instantly so the correct card is on top for the whole tween.
  zIndex: { duration: 0 },
};

function routeTransformTemplate(_: unknown, generatedTransform: string): string {
  const compactTransform = generatedTransform.replace(/\s/g, '');
  if (
    compactTransform === '' ||
    compactTransform === 'none' ||
    compactTransform === 'translateX(0)' ||
    compactTransform === 'translateX(0px)' ||
    compactTransform === 'translateX(0%)' ||
    compactTransform === 'translateZ(0px)' ||
    compactTransform === 'translateZ(0)'
  ) {
    return 'none';
  }

  return `${generatedTransform} translateZ(0)`;
}

/**
 * The exiting screen only needs to be a moving card. After it leaves the
 * present tree, drop its live DOM so the phone is not laying out two galleries
 * for the rest of the slide.
 */
function SlidingRouteBody({ children }: { children: ReactNode }) {
  const isPresent = useIsPresent();
  const [shell, setShell] = useState(false);

  if (!isPresent && !shell) {
    setShell(true);
  } else if (isPresent && shell) {
    setShell(false);
  }

  if (shell) {
    return <Box minH="100%" bg="bg" />;
  }
  return children;
}

function readHistoryIndex(): number | undefined {
  if (typeof window === 'undefined') return undefined;

  const index = window.history.state?.idx;
  return typeof index === 'number' ? index : undefined;
}

function isCardDetailPath(pathname: string): boolean {
  return Boolean(matchPath('/cards/:setCode/:cardSlug', pathname));
}

/** Own collection home or its set-stats sheet deep link. */
function isOwnCollectionStatsSurface(pathname: string): boolean {
  return (
    pathname === '/collection' ||
    Boolean(matchPath('/collection/sets/:setSlug', pathname))
  );
}

/**
 * Collection set-stats sheets are overlays on the collection page, not a new
 * screen. Animating `/collection` ↔ `/collection/sets/:slug` remounts the whole
 * page (AnimatePresence keys on pathname) and makes the sheet feel delayed.
 */
function isCollectionSetSheetNav(from: string, to: string): boolean {
  if (isOwnCollectionStatsSurface(from) && isOwnCollectionStatsSurface(to)) {
    return true;
  }
  const fromPublic =
    matchPath('/collections/:slug/sets/:setSlug', from) ??
    matchPath('/collections/:slug', from);
  const toPublic =
    matchPath('/collections/:slug/sets/:setSlug', to) ??
    matchPath('/collections/:slug', to);
  return Boolean(
    fromPublic &&
      toPublic &&
      fromPublic.params.slug &&
      fromPublic.params.slug === toPublic.params.slug,
  );
}

/** Community ↔ My Decks is an in-gallery scope flip, not a pushed screen. */
function isDeckGalleryScopeNav(from: string, to: string): boolean {
  return (
    (from === '/decks' || from === '/decks/my-decks') &&
    (to === '/decks' || to === '/decks/my-decks')
  );
}

/**
 * AnimatePresence remounts whenever this key changes. Collapse in-page scope
 * URLs (deck gallery, collection set-stats sheets) onto one key so the page
 * instance stays mounted while `location` updates.
 */
export function mobileRoutePresenceKey(pathname: string): string {
  if (pathname === '/decks' || pathname === '/decks/my-decks') {
    return '/decks';
  }
  if (isOwnCollectionStatsSurface(pathname)) {
    return '/collection';
  }
  const publicCollection =
    matchPath('/collections/:slug/sets/:setSlug', pathname) ??
    matchPath('/collections/:slug', pathname);
  if (publicCollection?.params.slug) {
    return `/collections/${publicCollection.params.slug}`;
  }
  return pathname;
}

function transitionDirection(
  navigationType: ReturnType<typeof useNavigationType>,
  previous: RouteSnapshot | undefined,
  historyIndex: number | undefined,
): TransitionDirection {
  if (navigationType === 'REPLACE') return 0;
  if (navigationType === 'PUSH') return 1;

  if (previous?.historyIndex !== undefined && historyIndex !== undefined) {
    if (historyIndex < previous.historyIndex) return -1;
    if (historyIndex > previous.historyIndex) return 1;
  }

  return -1;
}

export function MobileRouteTransition({
  children,
  location: locationOverride,
  transitionsEnabled = true,
}: MobileRouteTransitionProps) {
  const routerLocation = useLocation();
  const location = locationOverride ?? routerLocation;
  const navigationType = useNavigationType();
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
  const prefersReducedMotion = useReducedMotion();
  const historyIndex = readHistoryIndex();
  const previousRef = useRef<RouteSnapshot | undefined>(undefined);
  const previous = previousRef.current;
  // Only the active (transitions-enabled) panel may consume the app-back mark —
  // hidden keep-alive panels share the same navigationType tick.
  const animateAppBack = useMemo(
    () =>
      transitionsEnabled &&
      (navigationType === 'POP' || navigationType === 'REPLACE') &&
      consumeAppBackTransition(),
    [location.key, navigationType, transitionsEnabled],
  );
  const direction = animateAppBack ? -1 : transitionDirection(navigationType, previous, historyIndex);
  const currentChrome = getRouteChrome(location.pathname);
  const previousChrome = previous ? getRouteChrome(previous.pathname) : undefined;
  const isSamePathNavigation = previous?.pathname === location.pathname;
  const isCardDetailSwap =
    previous !== undefined &&
    isCardDetailPath(previous.pathname) &&
    isCardDetailPath(location.pathname);
  const isCollectionSetSheetSwap =
    previous !== undefined &&
    isCollectionSetSheetNav(previous.pathname, location.pathname);
  const isDeckGalleryScopeSwap =
    previous !== undefined &&
    isDeckGalleryScopeNav(previous.pathname, location.pathname);
  // Bottom-tab roots are parallel stacks, not a push hierarchy. Swapping
  // Cards ↔ Decks (etc.) must not slide like a detail push — only in-tab
  // forward nav / in-app back should animate.
  const previousTab = previous ? mobileTabIdForPath(previous.pathname) : null;
  const currentTab = mobileTabIdForPath(location.pathname);
  const isPrimaryTabSwap =
    previousTab !== null && currentTab !== null && previousTab !== currentTab;
  // Only push (forward nav) and the in-app back button (`animateAppBack`, which
  // marks itself) run our slide. Raw `POP` from the browser/OS back button or an
  // interactive edge-swipe is left to the platform's own gesture animation —
  // running ours on top of it double-animates and flashes the screen.
  // Card-detail → card-detail, collection set-stats sheets, and Community ↔
  // My Decks are in-page updates, not pushed screens; animating them remounts
  // the page (AnimatePresence keys on the presence key) and stalls the UI.
  const shouldAnimate =
    transitionsEnabled &&
    isMobile &&
    !prefersReducedMotion &&
    previous !== undefined &&
    !isSamePathNavigation &&
    !isPrimaryTabSwap &&
    direction !== 0 &&
    !isCardDetailSwap &&
    !isCollectionSetSheetSwap &&
    !isDeckGalleryScopeSwap &&
    (navigationType === 'PUSH' || animateAppBack) &&
    !currentChrome.disableMobileRouteTransition &&
    !previousChrome?.disableMobileRouteTransition;

  const presenceKey = mobileRoutePresenceKey(location.pathname);

  useEffect(() => {
    previousRef.current = {
      pathname: location.pathname,
      historyIndex,
    };
  }, [historyIndex, location.pathname]);

  // A freshly pushed screen must start at its own top, mirroring native push.
  useLayoutEffect(() => {
    if (shouldAnimate && direction > 0 && typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }, [direction, location.pathname, shouldAnimate]);

  // Desktop: no slide chrome, no keep-alive absolute shell to fill.
  if (!isMobile) {
    return <>{children(location)}</>;
  }

  // Mobile always keeps this AnimatePresence tree mounted. Toggling
  // `shouldAnimate` (e.g. after a PUSH settles, or when DeckBuilderPage
  // `replace`s to clear location.state) must NOT swap to a different React
  // tree — that remounted the destination page and wiped in-memory /
  // location.state handoffs such as deck remix + preset legend.
  const animDirection: TransitionDirection = shouldAnimate ? direction : 0;

  return (
    <Box
      // Fill the keep-alive absolute panel without making route content a
      // flex-shrink participant (see MobileTabKeepAlive).
      h="100%"
      minH={0}
      minW={0}
      position="relative"
      overflowX="clip"
    >
      <AnimatePresence mode="popLayout" initial={false} custom={animDirection}>
        <motion.div
          key={presenceKey}
          custom={animDirection}
          variants={routeTransitionVariants}
          initial={shouldAnimate ? 'enter' : false}
          animate="center"
          exit="exit"
          transition={shouldAnimate ? routeTransition : immediateRouteTransition}
          transformTemplate={routeTransformTemplate}
          style={{
            position: 'relative',
            minHeight: '100%',
            minWidth: 0,
            width: '100%',
            // NOTE: do NOT add `will-change: transform` here. It permanently
            // establishes a containing block for `position: fixed` descendants,
            // which breaks pages with fixed headers / sticky bars (e.g. the deck
            // builder). framer applies the transform only during the slide, so
            // fixed elements anchor to the viewport at rest and ride along only
            // while animating — which is the desired behaviour.
          }}
        >
          {/* Opaque surface so the screen beneath never shows through the moving card. */}
          <Box minH="100%" minW={0} bg="bg">
            <SlidingRouteBody>{children(location)}</SlidingRouteBody>
          </Box>
        </motion.div>
      </AnimatePresence>
    </Box>
  );
}
